import { EMAIL_CONFIG } from '../config/secrets';
import { buildInviteLandingUrl, getOrgInviteOrigin } from '../lib/inviteOrigin';

interface EmailParams {
  to: string;
  subject: string;
  html: string;
  text?: string;
}

export class EmailService {
  /** Same-origin proxy avoids MailerSend browser CORS in production / local Vite. */
  private static getApiUrl(): string {
    if (typeof window === 'undefined') return 'https://api.mailersend.com/v1/email';
    return `${window.location.origin}/api/mailersend/v1/email`;
  }

  static async sendEmail({ to, subject, html, text }: EmailParams): Promise<boolean> {
    try {
      // Do not send MailerSend tokens from the browser. Prefer Lambda / server proxy.
      const response = await fetch(this.getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: EMAIL_CONFIG.from,
          to: [{ email: to }],
          subject,
          html,
          text: text || html.replace(/<[^>]*>/g, ''), // Strip HTML tags for plain text version
          reply_to: EMAIL_CONFIG.replyTo
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        // MailerSend API Error logged
        throw new Error(`Failed to send email: ${(error as { message?: string }).message || response.statusText}`);
      }

      return true;
    } catch (error) {
      // Error sending email logged
      throw error;
    }
  }

  static async sendInvitationEmail(
    to: string,
    token: string,
    role: string,
    organizationName?: string,
    inviteOrigin?: string,
  ): Promise<boolean> {
    const origin = inviteOrigin?.replace(/\/+$/, '') || getOrgInviteOrigin();
    const landingPageUrl = buildInviteLandingUrl(role, token, origin);
    const productName = origin.includes('miraplus') ? 'MiraPlus' : 'TinyWins';

    const subject = organizationName 
      ? `You've been invited to join ${organizationName} on ${productName} as a ${role}`
      : `You've been invited to join ${productName} as a ${role}`;
    
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #4F46E5;">Welcome to ${productName}!</h2>
        ${organizationName 
          ? `<p>You've been invited to join <strong>${organizationName}</strong> on ${productName} as a <strong>${role}</strong>.</p>`
          : `<p>You've been invited to join ${productName} as a <strong>${role}</strong>.</p>`
        }
        <p>Click the button below to complete your registration:</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${landingPageUrl}" 
             style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            Complete Registration
          </a>
        </div>
        <p>If the button doesn't work, you can copy and paste this link into your browser:</p>
        <p style="color: #4F46E5;">${landingPageUrl}</p>
        <p>This invitation stays valid until you complete registration.</p>
        <p>If you didn't request this invitation, please ignore this email.</p>
      </div>
    `;

    return this.sendEmail({ to, subject, html });
  }

  static async sendQuestionnaireAssignmentEmail(params: {
    to: string;
    parentName?: string;
    childName: string;
    questionnaireName: string;
    organizationName?: string;
    therapistName?: string;
    actionUrl: string;
    isNewUser: boolean;
  }): Promise<boolean> {
    const { to, childName, questionnaireName, organizationName, therapistName, actionUrl, isNewUser } = params;
    const orgLine = organizationName ? `<strong>${organizationName}</strong>` : 'your care team';
    const therapistLine = therapistName ? ` ${therapistName} from ${orgLine}` : ` ${orgLine}`;
    const subject = `Please complete ${questionnaireName} for ${childName}`;
    const cta = isNewUser ? 'Register & start assessment' : 'Log in & start assessment';

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #4F46E5;">Assessment assigned</h2>
        <p>${therapistLine} has asked you to complete <strong>${questionnaireName}</strong> for <strong>${childName}</strong>.</p>
        <p>Your answers help personalize support and track progress.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${actionUrl}"
             style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            ${cta}
          </a>
        </div>
        <p>If the button doesn't work, copy this link:</p>
        <p style="color: #4F46E5; word-break: break-all;">${actionUrl}</p>
      </div>
    `;

    return this.sendEmail({ to, subject, html });
  }

  static async sendAssessmentCompletedEmail(params: {
    to: string;
    questionnaireName: string;
    childName: string;
    actionUrl: string;
  }): Promise<boolean> {
    const { to, questionnaireName, childName, actionUrl } = params;
    const subject = `${childName}: ${questionnaireName} completed`;
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #4F46E5;">Assessment submitted</h2>
        <p>A parent completed <strong>${questionnaireName}</strong> for <strong>${childName}</strong>.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${actionUrl}"
             style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            View responses
          </a>
        </div>
        <p style="color: #4F46E5; word-break: break-all;">${actionUrl}</p>
      </div>
    `;
    return this.sendEmail({ to, subject, html });
  }
} 
